import React, { useState } from "react";

interface StudentsTabProps {
  data: any;
}

export default function StudentsTab({ data }: StudentsTabProps) {
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("name");

  if (!data.students || data.students.length === 0) {
    return (
      <div className="alert alert-info">No student activity data available</div>
    );
  }

  let filteredStudents = [...data.students];

  // Filter by engagement status
  if (filterStatus === "at-risk") {
    filteredStudents = filteredStudents.filter((s: any) => s.isAtRisk);
  } else if (filterStatus === "active") {
    filteredStudents = filteredStudents.filter(
      (s: any) => s.engagementStatus === "active"
    );
  } else if (filterStatus === "inactive") {
    filteredStudents = filteredStudents.filter(
      (s: any) => s.engagementStatus === "inactive"
    );
  }

  // Sort students
  filteredStudents.sort((a: any, b: any) => {
    switch (sortBy) {
      case "name":
        return (a.name || "").localeCompare(b.name || "");
      case "resources":
        return (b.resourcesAccessed || 0) - (a.resourcesAccessed || 0);
      case "completion":
        return (b.resourcesCompleted || 0) - (a.resourcesCompleted || 0);
      case "time":
        return (b.totalTimeSpent || 0) - (a.totalTimeSpent || 0);
      case "tests":
        return (b.testsAttempted || 0) - (a.testsAttempted || 0);
      case "score":
        return (b.averageTestScore || 0) - (a.averageTestScore || 0);
      default:
        return 0;
    }
  });

  const atRiskCount = data.students.filter((s: any) => s.isAtRisk).length;
  const activeCount = data.students.filter(
    (s: any) => s.engagementStatus === "active"
  ).length;

  return (
    <div className="students-tab">
      {/* Summary Cards */}
      <div className="students-summary">
        <div className="summary-card">
          <div className="summary-icon">👥</div>
          <div className="summary-content">
            <div className="summary-label">Total Students</div>
            <div className="summary-value">{data.students.length}</div>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🎯</div>
          <div className="summary-content">
            <div className="summary-label">Active</div>
            <div className="summary-value">{activeCount}</div>
          </div>
        </div>
        <div className="summary-card alert-card">
          <div className="summary-icon">⚠️</div>
          <div className="summary-content">
            <div className="summary-label">At Risk</div>
            <div className="summary-value">{atRiskCount}</div>
          </div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="students-controls">
        <div className="filter-group">
          <label>Filter by Status:</label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="form-control"
          >
            <option value="all">All Students</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
            <option value="at-risk">At Risk Only</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="form-control"
          >
            <option value="name">Name</option>
            <option value="resources">Resources Accessed</option>
            <option value="completion">Resources Completed</option>
            <option value="time">Time Spent</option>
            <option value="tests">Tests Attempted</option>
            <option value="score">Test Score</option>
          </select>
        </div>
      </div>

      {/* Students Table */}
      <div className="analytics-card">
        <h5>👥 Student Details ({filteredStudents.length})</h5>
        <div className="table-responsive">
          <table className="table table-hover analytics-table">
            <thead>
              <tr>
                <th>Student Name</th>
                <th>Status</th>
                <th>Resources Accessed</th>
                <th>Resources Completed</th>
                <th>Time Spent</th>
                <th>Tests Attempted</th>
                <th>Avg Test Score</th>
                <th>Last Activity</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student: any, index: number) => (
                <tr key={index} className={student.isAtRisk ? "at-risk-row" : ""}>
                  <td>
                    <strong>{student.name || "Unknown Student"}</strong>
                    {student.isAtRisk && (
                      <span className="at-risk-badge" title="Student at risk">
                        ⚠️
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        student.engagementStatus === "active"
                          ? "status-active"
                          : student.engagementStatus === "moderate"
                            ? "status-moderate"
                            : "status-inactive"
                      }`}
                    >
                      {student.engagementStatus || "unknown"}
                    </span>
                  </td>
                  <td>{student.resourcesAccessed || 0}</td>
                  <td>
                    {student.resourcesCompleted || 0}
                    {student.resourcesAccessed > 0 && (
                      <span className="completion-pct">
                        {" "}
                        (
                        {Math.round(
                          ((student.resourcesCompleted || 0) /
                            student.resourcesAccessed) *
                            100
                        )}
                        %)
                      </span>
                    )}
                  </td>
                  <td>{Math.round(student.totalTimeSpent || 0)} min</td>
                  <td>{student.testsAttempted || 0}</td>
                  <td>
                    {student.averageTestScore > 0
                      ? `${Math.round(student.averageTestScore)}%`
                      : "N/A"}
                  </td>
                  <td>
                    {student.lastActivity
                      ? new Date(student.lastActivity).toLocaleDateString()
                      : "No activity"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* At Risk Students Alert */}
      {atRiskCount > 0 && (
        <div className="analytics-card alert-card">
          <h5>⚠️ Students Requiring Attention</h5>
          <p>
            <strong>{atRiskCount}</strong> student(s) are at risk due to low
            engagement or no recent activity. Consider reaching out to:
          </p>
          <ul>
            {data.students
              .filter((s: any) => s.isAtRisk)
              .map((student: any, index: number) => (
                <li key={index}>
                  <strong>{student.name}</strong> - Last active:{" "}
                  {student.lastActivity
                    ? new Date(student.lastActivity).toLocaleDateString()
                    : "Never"}
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
